import type { CSSProperties } from 'react';

export type FormThemeId =
  | 'EUPAGO'
  | 'LIGHT'
  | 'DARK'
  | 'PASTEL'
  | 'CORPORATE'
  | 'PREMIUM'
  | 'VIBRANT'
  | 'MINIMALIST'
  | 'HIGH_CONTRAST'
  | 'CUSTOM';

export type FormThemeBase = 'LIGHT' | 'DARK';

type ThemePalette = {
  page: string;
  surface: string;
  text: string;
  muted: string;
  accent: string;
  accentText: string;
  soft: string;
  border: string;
  background?: string;
  shadow?: string;
  radius?: string;
};

export const FORM_THEMES: Array<{
  id: FormThemeId;
  name: string;
  description: string;
  preview: string[];
}> = [
  { id: 'EUPAGO', name: 'Eupago', description: 'Verde da marca e superfícies claras.', preview: ['#123b37', '#10d7b5', '#f4f8f7'] },
  { id: 'LIGHT', name: 'Claro', description: 'Branco, bege e tons naturais.', preview: ['#fffdf8', '#9a6b3f', '#f5f1e8'] },
  { id: 'DARK', name: 'Escuro', description: 'Preto suave e cinzento profundo.', preview: ['#0b0d10', '#8b9baa', '#f8fafc'] },
  { id: 'PASTEL', name: 'Pastel', description: 'Cores suaves e pouco saturadas.', preview: ['#fdf8fb', '#8e7cc3', '#d8eee7'] },
  { id: 'CORPORATE', name: 'Corporativo', description: 'Azul-marinho, cinzento e branco.', preview: ['#102a43', '#164e7a', '#eef3f8'] },
  { id: 'PREMIUM', name: 'Premium', description: 'Fundo escuro com dourado intenso e texto claro.', preview: ['#0b0a08', '#d8ae52', '#fff8e8'] },
  { id: 'VIBRANT', name: 'Vibrante', description: 'Coral, laranja e gradiente moderno.', preview: ['#f05a3c', '#ffb15c', '#fff1f2'] },
  { id: 'MINIMALIST', name: 'Minimalista', description: 'Espaço, conteúdo e contornos discretos.', preview: ['#ffffff', '#111827', '#e5e7eb'] },
  { id: 'HIGH_CONTRAST', name: 'Alto contraste', description: 'Preto, branco e amarelo acessível.', preview: ['#000000', '#ffdf00', '#ffffff'] },
  { id: 'CUSTOM', name: 'Marca personalizada', description: 'Escolhe o HEX dos botões e destaques.', preview: ['#ffffff', '#64748b', '#111827'] },
];

const PALETTES: Record<Exclude<FormThemeId, 'CUSTOM'>, ThemePalette> = {
  EUPAGO: { page: '#f4f8f7', surface: '#ffffff', text: '#172321', muted: '#667773', accent: '#123b37', accentText: '#ffffff', soft: '#dcfbf5', border: '#dce7e4' },
  LIGHT: { page: '#f5f1e8', surface: '#fffdf8', text: '#292524', muted: '#78716c', accent: '#8a5c35', accentText: '#ffffff', soft: '#f1e8da', border: '#ded4c5' },
  DARK: { page: '#0b0d10', surface: '#15191e', text: '#f8fafc', muted: '#a1a1aa', accent: '#9cabb8', accentText: '#101418', soft: '#242a31', border: '#343b44' },
  PASTEL: { page: '#fdf8fb', surface: '#ffffff', text: '#43384a', muted: '#7f7187', accent: '#7662ad', accentText: '#ffffff', soft: '#f0eafd', border: '#e8dff0' },
  CORPORATE: { page: '#eef3f8', surface: '#ffffff', text: '#102a43', muted: '#627d98', accent: '#164e7a', accentText: '#ffffff', soft: '#e6f0f8', border: '#cbd9e6' },
  PREMIUM: { page: '#0b0a08', surface: '#18150f', text: '#fff8e8', muted: '#dfca98', accent: '#d8ae52', accentText: '#171108', soft: '#2b2417', border: '#6b5528', shadow: '0 30px 80px rgba(0,0,0,0.42)' },
  VIBRANT: { page: '#fff5ef', surface: '#ffffff', text: '#3b1d18', muted: '#8a4e43', accent: '#e94f32', accentText: '#ffffff', soft: '#fff0e8', border: '#ffc7b4', background: 'linear-gradient(135deg, #fff7ed 0%, #fff1f2 52%, #fef3c7 100%)' },
  MINIMALIST: { page: '#ffffff', surface: '#ffffff', text: '#111827', muted: '#6b7280', accent: '#111827', accentText: '#ffffff', soft: '#f9fafb', border: '#e5e7eb', shadow: 'none', radius: '0.75rem' },
  HIGH_CONTRAST: { page: '#000000', surface: '#050505', text: '#ffffff', muted: '#ffffff', accent: '#ffdf00', accentText: '#000000', soft: '#1a1a1a', border: '#ffffff', shadow: '0 0 0 2px #ffffff' },
};

export const isValidHexColor = (value?: string | null) => /^#[0-9a-f]{6}$/i.test(String(value ?? '').trim());

const rgb = (hex: string) => [1, 3, 5].map((index) => Number.parseInt(hex.slice(index, index + 2), 16));
const luminance = (hex: string) => rgb(hex).map((channel) => {
  const value = channel / 255;
  return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}).reduce((result, value, index) => result + value * [0.2126, 0.7152, 0.0722][index], 0);

export const contrastRatio = (first: string, second: string) => {
  const [lighter, darker] = [luminance(first), luminance(second)].sort((a, b) => b - a);
  return (lighter + 0.05) / (darker + 0.05);
};

export const getAccessibleTextColor = (background: string) =>
  contrastRatio(background, '#ffffff') >= contrastRatio(background, '#111827') ? '#ffffff' : '#111827';

export const getThemePalette = (
  theme?: string | null,
  customAccent?: string | null,
  customBase?: string | null,
): ThemePalette => {
  const normalized = String(theme ?? 'EUPAGO').toUpperCase() as FormThemeId;
  if (normalized !== 'CUSTOM' && normalized in PALETTES) return PALETTES[normalized];

  const accent = isValidHexColor(customAccent) ? String(customAccent).toUpperCase() : '#2563EB';
  const dark = String(customBase ?? 'LIGHT').toUpperCase() === 'DARK';
  return {
    page: dark ? '#0b1220' : '#f8fafc',
    surface: dark ? '#111827' : '#ffffff',
    text: dark ? '#f8fafc' : '#111827',
    muted: dark ? '#cbd5e1' : '#64748b',
    accent,
    accentText: getAccessibleTextColor(accent),
    soft: dark ? '#1e293b' : `${accent}14`,
    border: dark ? '#334155' : '#dbe3ec',
  };
};

export const getThemeStyle = (theme?: string | null, customAccent?: string | null, customBase?: string | null) => {
  const palette = getThemePalette(theme, customAccent, customBase);
  return {
    '--form-page': palette.page,
    '--form-page-background': palette.background ?? palette.page,
    '--form-surface': palette.surface,
    '--form-text': palette.text,
    '--form-muted': palette.muted,
    '--form-accent': palette.accent,
    '--form-accent-text': palette.accentText,
    '--form-soft': palette.soft,
    '--form-border': palette.border,
    '--form-shadow': palette.shadow ?? '0 30px 80px rgba(18,55,50,0.12)',
    '--form-radius': palette.radius ?? '2rem',
  } as CSSProperties;
};
